import { ScheduledBankPaymentGroupStateEnum } from '@/features/payments/types/BatchPayment';
import { TPaymentListItem } from '../types/ManagePayments';
import { useMultiSignApprovers } from './useMultiSignApprovers';

/**
 * The shared "what should this viewer see about the approval decision" derivation - previously
 * hand-rolled separately (and inconsistently) in BatchModal and ScheduledPaymentModal, which let
 * the two drift out of alignment. Both should call this instead of useMultiSignApprovers
 * directly whenever they need these display-oriented flags, so a future fix only has to land
 * once.
 *
 * Three of the flags below are deliberately kept separate, not folded into one:
 * - `isAwaitingAdditionalApproval` (hasAnyApproval - ANY signatory, not just this viewer) drives
 *   the wider "Additional approval required" status label every remaining approver sees.
 * - `isCurrentUserApproved` (hasPersistedApproval - this viewer specifically) drives anything
 *   that personally credits/addresses this viewer - the "You have approved this payment" banner
 *   and the "Approved by" row. It must not fire on someone else's approval.
 * - `isApprovalRequiredForViewer` (pending, THIS viewer hasn't approved yet, and has authority
 *   to) drives whether the "to approve" framing (e.g. "View payments to approve") still applies
 *   to them - true regardless of whether someone else already approved, since that doesn't
 *   change whether this viewer personally still has something to do.
 *
 * `isAwaitingAdditionalApproval` is deliberately ALSO gated on `!isNoApprovalAuthority`, not just
 * `hasAnyApproval` - hasAnyApproval only asks "did anyone at all approve", which says nothing
 * about whether THIS viewer even has a signing slot on the action. Without the exclusion here, a
 * non-signatory viewing a payment a different signatory already approved would have both
 * isAwaitingAdditionalApproval and isNoApprovalAuthority true at once - a caller that happened to
 * check the wrong one first would show "Additional approval required" instead of the no-authority
 * status. Excluding it here, once, means every current and future caller gets the right answer
 * regardless of which order they check these flags in - the two are now mutually exclusive by
 * construction, not just by callers remembering to order their checks correctly.
 *
 * All four (plus `isNoApprovalAuthority`) are pre-scoped to `payment.state === PENDING_APPROVAL`
 * here, once, rather than each caller repeating that same guard against the raw hook fields.
 */
export const useApprovalDisplayState = (
  payment: TPaymentListItem | null,
  /** Passed straight through to useMultiSignApprovers' own `enabled` - a modal's isOpen, or
   * PaymentStatusPill's own "only fetch for a PENDING_APPROVAL row" gate. */
  enabled: boolean,
  currentCustomerId?: string
) => {
  const {
    hasPersistedApproval,
    hasAnyApproval,
    hasNoApprovalAuthority,
    isLoading: isLoadingApprovers,
    refetch
  } = useMultiSignApprovers(payment?.multiSignActionId, enabled, currentCustomerId, payment?.state);

  const isPendingApproval = payment?.state === ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL;
  const isNoApprovalAuthority = isPendingApproval && hasNoApprovalAuthority;
  // See the module comment above for why this excludes isNoApprovalAuthority, not just
  // hasAnyApproval - isNoApprovalAuthority must be computed above this line, not below it.
  const isAwaitingAdditionalApproval =
    isPendingApproval && hasAnyApproval && !isNoApprovalAuthority;
  const isCurrentUserApproved = isPendingApproval && hasPersistedApproval;
  const isApprovalRequiredForViewer =
    isPendingApproval && !hasPersistedApproval && !hasNoApprovalAuthority;
  // None of the above is trustworthy yet while the authorisation fetch is still in flight - every
  // caller gates its own loading-state render on this rather than committing to a label/action
  // early (see each modal's own isLoadingApprovalDecision usage).
  const isLoadingApprovalDecision = isPendingApproval && isLoadingApprovers;

  return {
    // Raw fields, unscoped - still needed as-is by useScheduledPaymentAction's own options.
    hasPersistedApproval,
    hasAnyApproval,
    hasNoApprovalAuthority,
    isLoadingApprovers,
    refetch,
    // Display-ready, pre-scoped to PENDING_APPROVAL.
    isPendingApproval,
    isAwaitingAdditionalApproval,
    isCurrentUserApproved,
    isNoApprovalAuthority,
    isApprovalRequiredForViewer,
    isLoadingApprovalDecision
  };
};
